import { debounce, DebouncedFunc } from 'lodash-es';
import { useCallback, useEffect, useRef } from 'react';

export function scrollToBottom(element: HTMLDivElement): void {
  element.scroll({ top: element.scrollHeight, behavior: 'smooth' });
}

/**
 * A generic debounce hook that takes any function and a delay and does the clean up
 *
 * @param callback - The function to debounce
 * @param delay - The debounce delay in milliseconds
 * @returns The debounced function
 */
export function useDebounce<T extends (...args: any[]) => void>(
  callback: T,
  delay: number,
): DebouncedFunc<T> {
  const debouncedCallbackRef = useRef<DebouncedFunc<T> | null>(null);
  const memoizedCallback = useCallback(callback, [callback]);

  if (!debouncedCallbackRef.current) {
    debouncedCallbackRef.current = debounce(memoizedCallback, delay);
  }

  // Cleanup function to cancel debounce on unmount
  useEffect(() => {
    return () => {
      debouncedCallbackRef.current?.cancel();
    };
  }, []);

  return debouncedCallbackRef.current!;
}
